import type { JsonSchemaType } from '@gmn-portal/backend';
import { DateTime } from 'luxon';
import { useCustomer, useObjects } from '@/core/api';
import { useGlobalMetadata } from '@/core/api/global-metadata';
import { useIntl } from '@/core/i18n/use-intl';

import type { ResolvedFieldValueProps } from './types';

export function ResolvedFieldValue({ field, value, contract }: ResolvedFieldValueProps) {
  const { t } = useIntl();
  const { data: globalMetadata } = useGlobalMetadata();

  // Handle status field specifically
  if (field === 'status') {
    if (value === 'open' || value === 'closed') {
      return <span>{t(`contracts.status.${value}`)}</span>;
    }
  }

  // Handle metadata fields that may be nested objects/relationships
  if (field.startsWith('metadata.')) {
    const metadataKey = field.split('.')[1];

    const metadataSchema = globalMetadata?.rootContractMetadata as JsonSchemaType | undefined;

    // Check if this field has a specific type in the schema
    const fieldSchema = metadataSchema?.properties?.[metadataKey];

    if (fieldSchema?.type === 'date' && typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return <span>{date.toFormat('dd.MM.yyyy')}</span>;
      }
      return <span>{String(value)}</span>;
    }
  }

  // Handle dynamic metadata fields
  if (field.startsWith('dynamicMetadata.')) {
    const metadataKey = field.split('.')[1];
    const fieldSchema = contract?.dynamicMetadataSchema?.properties?.[metadataKey];

    if (fieldSchema?.type === 'date' && typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return <span>{date.toFormat('dd.MM.yyyy')}</span>;
      }
      return <span>{String(value)}</span>;
    }
  }

  // Handle objects field
  if (field === 'objects' && Array.isArray(value)) {
    return <ObjectsDisplay objectIds={value} />;
  }

  // Handle customer field
  if (field === 'customer' && typeof value === 'string') {
    return <CustomerDisplay customerId={value} />;
  }

  // Handle other fields
  if (typeof value === 'string') {
    return <span>{value}</span>;
  }

  if (typeof value === 'number') {
    return <span>{value}</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? t('common.yes') : t('common.no')}</span>;
  }

  // Fallback for other types
  return <span>{String(value)}</span>;
}

const ObjectsDisplay = ({ objectIds }: { objectIds: string[] }) => {
  const { data: objectsData, isLoading } = useObjects({
    where: {
      id: {
        in: objectIds,
      },
    },
    limit: objectIds.length,
    disabled: !objectIds || objectIds.length === 0,
  });

  if (isLoading) return <span>Loading...</span>;

  const objects = objectsData?.docs || [];
  if (objects.length === 0) return <span>-</span>;

  return <span>{objects.map((obj) => obj.name).join(', ')}</span>;
};

const CustomerDisplay = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);

  if (isLoading) return <span>Loading...</span>;
  return <span>{customer?.name || '-'}</span>;
};
